import React from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import TopCarousel from "./Home/TopCarousel";
import Reviews from "../Components/Reviews";
import Tours from "./Tours";
import { Col, Container, Row } from "react-bootstrap";
import { Image, Transformation, Placeholder } from "cloudinary-react";
import { Helmet } from "react-helmet";
import Banner from "./Banner";
import ScrollReveal from "./ScrollReveal";
import PageSEO from "./PageSEO";

const woodgrain = {
  backgroundImage:
    "url('https://res.cloudinary.com/american-legacy-tours/image/upload/f_auto,q_auto/img/wooden-texture2.webp')",
  backgroundRepeat: "repeat",
};

const Body = () => {
  return (
    <div style={woodgrain}>
      <PageSEO
        title="American Legacy Tours | Cincinnati Walking Tours"
        description="Cincinnati's award-winning walking tours — historic, haunted, underground &amp; food tours in Cincinnati, Newport &amp; Covington. 3,500+ five-star reviews. Book today!"
        path="/"
        schema={{
          "@context": "https://schema.org",
          "@type": ["LocalBusiness", "TouristAttraction"],
          "name": "American Legacy Tours",
          "description": "Cincinnati's award-winning walking tour company offering historic, haunted, underground, and food & drink tours in Cincinnati, Newport, and Covington.",
          "url": "https://www.americanlegacytours.com",
          "telephone": "+18599518560",
          "email": "questions@americanlegacytours.com",
          "image": "https://res.cloudinary.com/american-legacy-tours/image/upload/f_auto,q_auto/img/home/slider/slider-01",
          "address": { "@type": "PostalAddress", "addressLocality": "Cincinnati", "addressRegion": "OH", "postalCode": "45202", "addressCountry": "US" },
          "geo": { "@type": "GeoCoordinates", "latitude": 39.1031, "longitude": -84.5120 },
          "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "3500", "bestRating": "5", "worstRating": "1" },
          "priceRange": "$$",
          "sameAs": [
            "https://www.facebook.com/AmericanLegacyTours",
            "https://twitter.com/AmLegacyTours",
            "https://www.instagram.com/americanlegacycincy",
            "https://www.tripadvisor.com/Attraction_Review-g60993-d4432364-Reviews-American_Legacy_Tours-Cincinnati_Ohio.html"
          ]
        }}
      />
      <Helmet>
        <link
          rel="preload"
          as="image"
          fetchpriority="high"
          href="https://res.cloudinary.com/american-legacy-tours/image/upload/c_fill,h_700,w_1800/f_auto/v1/img/home/slider/slider-01"
        />
      </Helmet>

      <TopCarousel />

      {/* ── Intro ── */}
      <section style={woodgrain}>
        <Container className="home-intro">
          <ScrollReveal>
            <p className="glass-card" style={{ marginBottom: 0 }}>
              When you're looking for things to do in Cincinnati, American Legacy
              Tours has you covered with the best walking &amp; bus tours in
              Cincinnati, Newport, and Covington! Our walking tours range from
              Food and Drink Tours and Underground Tours to Historic Tours and
              Haunted Tours. Explore the city and experience Cincinnati like never
              before with historically entertaining tours from American Legacy Tours.
            </p>
          </ScrollReveal>
        </Container>
      </section>

      {/* ── All Tours ── */}
      <div style={{ ...woodgrain, textAlign: "center" }}>
        <ScrollReveal>
          <h1 className="section-title">All Tours</h1>
        </ScrollReveal>
        <Container style={{ paddingLeft: 0, paddingRight: 0 }}>
          <Tours />
        </Container>
      </div>

      {/* ── TripAdvisor ── */}
      <section className="tripadvisor-section">
        <Container>
          <ScrollReveal direction="scale">
            <Row className="justify-content-center text-center">
              <Col md={8}>
                <Image
                  cloudName="american-legacy-tours"
                  publicId="/img/home/tripadvisor-logo"
                  style={{ width: 320, height: 48 }}
                  width="320"
                  height="48"
                  loading="lazy"
                  fetch_format="auto"
                >
                  <Placeholder type="predominant" />
                  <Transformation height="48" width="320" />
                </Image>
                <p className="tripadvisor-awards">
                  2022, 2023, &amp; 2024 Travelers&rsquo; Choice Award &amp; Certificate of Excellence
                </p>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16, flexWrap: "wrap" }}>
                  <Image
                    cloudName="american-legacy-tours"
                    publicId="/img/home/tripadvisor1"
                    style={{ width: 119, height: 20 }}
                    width="119"
                    height="20"
                    loading="lazy"
                    fetch_format="auto"
                  >
                    <Placeholder type="predominant" />
                    <Transformation height="20" width="119" />
                  </Image>
                  <a
                    href="https://www.tripadvisor.com/Attraction_Review-g60993-d4432364-Reviews-American_Legacy_Tours-Cincinnati_Ohio.html"
                    rel="noopener noreferrer"
                    target="_blank"
                    style={{ color: "#000", fontWeight: 600 }}
                  >
                    <span id="tripadvisorreviewcount">3,304</span> Reviews
                  </a>
                  <a
                    href="https://www.tripadvisor.com/Attraction_Review-g60993-d4432364-Reviews-American_Legacy_Tours-Cincinnati_Ohio.html"
                    className="btn-brand"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View on TripAdvisor
                  </a>
                </div>
              </Col>
            </Row>
          </ScrollReveal>
        </Container>
      </section>

      {/* ── Promo Banner ── */}
      <section>
        <Banner />
      </section>

      {/* ── Reviews ── */}
      <section className="reviews-section">
        <Container>
          <ScrollReveal>
            <Row style={{ marginLeft: 0, marginRight: 0 }}>
              <Reviews numToDisplay={4} />
            </Row>
          </ScrollReveal>
          <Row>
            <Col style={{ textAlign: "center", marginTop: 32 }}>
              <a href="/reviews" className="btn-outline-light-custom">
                Read More Reviews
              </a>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ── Questions + Private Tours ── */}
      <section className="private-section">
        <Container>
          <Row className="align-items-center">
            <Col md={7} className="mb-4 mb-md-0">
              <ScrollReveal direction="left">
                <Image
                  className="w-100"
                  cloudName="american-legacy-tours"
                  publicId="/img/home/private_tour"
                  style={{ borderRadius: 8 }}
                  loading="lazy"
                  fetch_format="auto"
                >
                  <Placeholder type="predominant" />
                  <Transformation height="360" width="540" crop="fill" />
                </Image>
              </ScrollReveal>
            </Col>
            <Col md={5}>
              <ScrollReveal direction="right" delay={100}>
                <h3>Questions?</h3>
                <p>
                  If you have any other questions, we'd be happy to help. Check out our{" "}
                  <a href="/faq">Frequently Asked Questions</a> or{" "}
                  <a href="/contact-us">contact us directly</a>.
                </p>
                <a href="tel:8599518560" className="btn-brand" style={{ marginBottom: 32, display: "inline-block" }}>
                  (859) 951-8560
                </a>

                <h3 style={{ marginTop: 32 }}>Private Tours</h3>
                <p>
                  American Legacy Tours are the perfect way for your company, family, and friends
                  to spend time together. Each tour can be customized for your group — we can
                  handle transportation, meals, and lodging too.
                </p>
                <a href="/private-tours" className="btn-brand">
                  Inquire About Private Tours
                </a>
              </ScrollReveal>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default Body;
